import React from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { REQUIRED_CHECKS, verificationState } from '../../../util/listingVerification';

import css from './SectionVerificationMaybe.module.css';

/**
 * What has been checked on this listing, and by whom — from the record only a
 * trusted server route can write (util/listingVerification.js).
 *
 * Renders nothing unless at least one check passed against the listing's
 * current facts: no record, a revoked record, or a record made before a
 * material edit all show nothing, so the page never implies a check that does
 * not apply to what the buyer is looking at. Failed checks are not published
 * here; they are for the Skip team to act on.
 *
 * While the checklist awaits approval (CHECKLIST_APPROVED) the heading is
 * never "Verified Listing"; passed checks are still listed by name.
 *
 * Evidence references and reviewer names are never in the listing's public
 * data, so there is nothing private for this component to leak.
 */
const SectionVerificationMaybe = props => {
  const { listing, now, checklistApproved } = props;
  const intl = useIntl();
  const state = verificationState(listing, {
    ...(now ? { now } : {}),
    ...(checklistApproved === undefined ? {} : { checklistApproved }),
  });
  const passed = state.current.filter(c => c.result === 'pass');
  if (passed.length === 0) return null;

  const dateOf = iso => intl.formatDate(new Date(iso), { dateStyle: 'long', timeZone: 'UTC' });
  const ordered = REQUIRED_CHECKS.map(id => passed.find(c => c.id === id)).filter(Boolean);

  return (
    <section className={css.root} aria-labelledby="listing-verification-heading">
      <h2 id="listing-verification-heading" className={css.heading}>
        {state.verified ? (
          <FormattedMessage id="SectionVerification.verifiedHeading" />
        ) : (
          <FormattedMessage id="SectionVerification.partialHeading" />
        )}
      </h2>
      <ul className={css.checks}>
        {ordered.map(c => (
          <li key={c.id} className={css.check}>
            <span className={css.checkName}>
              <FormattedMessage id={`SectionVerification.check.${c.id}`} />
            </span>
            <span className={css.checkMeta}>
              <FormattedMessage
                id="SectionVerification.checkedBy"
                values={{ provider: c.provider, date: dateOf(c.checkedAt) }}
              />
            </span>
          </li>
        ))}
      </ul>
      <p className={css.explanation}>
        <FormattedMessage
          id={
            state.verified
              ? 'SectionVerification.verifiedExplanation'
              : state.reason === 'checklist pending approval'
              ? 'SectionVerification.pendingExplanation'
              : 'SectionVerification.partialExplanation'
          }
        />
      </p>
      <p className={css.explanation}>
        <FormattedMessage id="SectionVerification.limits" />
      </p>
    </section>
  );
};

export default SectionVerificationMaybe;
